import './style.css';

import React, { useState } from 'react';
import { t, useLocalization } from '../../l10n/l10n';
import { useEContext } from '../../EContext';
import { Button, Modal, Input, Checkbox } from '@economic/gaudi';
import isValidEmail from '../../../util/utilities/isValidEmail';
import isValidName from '../../../util/utilities/isValidName';
import internalApiClient from '../../../util/api/internalApiClient';
import { Redirect } from 'react-router-dom';

enum Steps {
    Verify = 1,
    Done = 2,
}

function UserLinkedToVismaConnect({ headerText, contentText, onOk }) {
    return (
        <div className="vismaconnect-popups message-popup" style={{ maxWidth: '450px' }}>
            <div className="vismaconnect-popups message-popup-head">
                <span className="vismaconnect-popups popup__head__lbl">{headerText}</span>
            </div>
            <div className="vismaconnect-popups message-popup-content">{contentText}</div>

            <div>
                <Button type="button" appearance="primary" onClick={onOk}>
                    {' '}
                    {t`SHARED_CONFIRM_YES`}{' '}
                </Button>
            </div>
        </div>
    );
}

function FooterError({ error }) {
    return (
        <div className="vismaconnect-popups error-message" style={{ maxWidth: '750px' }}>
            <div className="vismaconnect-popups error-message-body">{error}</div>
        </div>
    );
}

const SimpleMarkupTranslator = (text: string) => {
    return text
        .replace(/\[PARAGRAPH_START\]/g, '<p>')
        .replace(/\[PARAGRAPH_END\]/g, '</p>')
        .replace(/\[BOLD_START\]/g, '<strong>')
        .replace(/\[BOLD_END\]/g, '</strong>')
        .replace(/\[ITALIC_START\]/g, '<em>')
        .replace(/\[ITALIC_END\]/g, '</em>');
};

const CheckBoxControlWithHtml = ({ value, label, onChange }) => (
    <div className="vismaconnect-popups controls form__ctrl  checkbox">
        <label className="vismaconnect-popups rich-label">
            <div>
                <Checkbox checked={value} onChange={({ target: { checked } }) => onChange(checked)} />
                <span className="vismaconnect-popups label labelExpanded" dangerouslySetInnerHTML={{ __html: label }}></span>
            </div>
        </label>
    </div>
);

interface Props {
    enforcePopup: boolean;
    displayNewsletter: boolean;
    isForInvite: boolean;
    firstName: string;
    lastName: string;
    email: string;
    onDone: () => void;
}

export default function VismaConnectVerifyInformationDialog({
    enforcePopup,
    displayNewsletter,
    isForInvite,
    firstName,
    lastName,
    email,
    onDone,
}: Props) {
    const loadingTexts = useLocalization(textIds);
    const { identity } = useEContext();
    const [show, setShow] = useState(true);
    const [step, setStep] = useState(Steps.Verify);
    const [state, setState] = useState({
        firstName: firstName,
        lastName: lastName,
        email: '', // to start with an empty email initially.
        error: null,
        isNewsletterEnabled: false,
        disableJoinButton: false,
        finalMessageHader: '',
        finalMessageContent: '',
        redirect: '',
    });

    if (loadingTexts || !identity || !show) {
        return null;
    }

    const getVismaConnectErrorPageUrl = () => {
        const redirectUrl = window.top.firebird.Econ.applicationUrl('/vc/error.aspx?error=14');
        const url = '/logout?redirectUrl=' + encodeURIComponent(redirectUrl);
        return url;
    };

    const subscribeToNewsletter = async requestModel => {
        await internalApiClient.post(`/users/subscribe`, requestModel);
    };

    const linkCurrentUser = async requestModel => {
        await internalApiClient
            .post(`/users/link`, requestModel)
            .then(async response => {
                state.disableJoinButton = false;

                if (state.isNewsletterEnabled) {
                    await subscribeToNewsletter(requestModel);
                }

                setStep(Steps.Done);

                if (enforcePopup) {
                    setState({ ...state, redirect: getVismaConnectErrorPageUrl() });
                } else {
                    setState({ ...state, finalMessageHader: response.data.header, finalMessageContent: response.data.content });
                }
            })
            .catch(error => {
                setState({ ...state, error: error.error || error.response.data, disableJoinButton: false });
            });
    };

    var titleText = isForInvite ? t`CONNECT_VERIFY_DETAILS_MODAL_TITLE_INVITE` : t`CONNECT_VERIFY_DETAILS_MODAL_TITLE`;
    var contentText = SimpleMarkupTranslator(
        isForInvite ? t`CONNECT_VERIFY_DETAILS_MODAL_CONTENT_INVITE` : t`CONNECT_VERIFY_DETAILS_MODAL_CONTENT`
    );
    var joinButtonText = isForInvite
        ? t`CONNECT_VERIFY_DETAILS_MODAL_JOIN_CONNECT_INVITE`
        : t`CONNECT_VERIFY_DETAILS_MODAL_JOIN_CONNECT`;

    if (state.redirect) {
        return <Redirect to={state.redirect} />;
    }

    if (step === Steps.Done) {
        return (
            <Modal visible showCloseButton={false} hideOnEsc={false} hideOnClickOutside={false}>
                <UserLinkedToVismaConnect
                    headerText={state.finalMessageHader}
                    contentText={state.finalMessageContent}
                    onOk={() => {
                        onDone();
                        setShow(false);
                    }}
                />
            </Modal>
        );
    }

    return (
        <Modal visible={show} showCloseButton={!enforcePopup} hideOnEsc={!enforcePopup} hideOnClickOutside={false}>
            <div className="vismaconnect-popups connect-verify-email-main">
                <div className="vismaconnect-popups connect-verify-email">
                    <h1 className="vismaconnect-popups connect-verify-details-title">{titleText}</h1>

                    <p className="vismaconnect-popups connect-verify-details-subtitle">{t`CONNECT_VERIFY_DETAILS_MODAL_SUBTITLE`}</p>

                    <p dangerouslySetInnerHTML={{ __html: contentText }} />

                    <label>
                        {t`CONNECT_VERIFY_DETAILS_MODAL_LABEL_EMAIL`}
                        <Input
                            className="vismaconnect-popups connect-verify-email-input"
                            type="email"
                            value={state.email}
                            onChange={e => onEmailChange(e.target.value)}
                            required
                        />
                    </label>

                    <label>
                        {t`CONNECT_VERIFY_DETAILS_MODAL_LABEL_FIRST_NAME`}
                        <Input
                            className="vismaconnect-popups connect-verify-email-input"
                            type="text"
                            value={state.firstName}
                            onChange={e => onFirstNameChange(e.target.value)}
                            required
                        />
                    </label>

                    <label>
                        {t`CONNECT_VERIFY_DETAILS_MODAL_LABEL_LAST_NAME`}
                        <Input
                            className="vismaconnect-popups connect-verify-email-input"
                            type="text"
                            value={state.lastName}
                            onChange={e => onLastNameChange(e.target.value)}
                            required
                        />
                    </label>

                    {!displayNewsletter || isForInvite ? (
                        <div style={{ paddingTop: '20px' }} />
                    ) : (
                        <div>
                            <p className="vismaconnect-popups newsletter-info-paragraph">{t`CONNECT_VERIFY_CHECKBOX1`}</p>
                            <CheckBoxControlWithHtml
                                label={t`CONNECT_VERIFY_CHECKBOX2`}
                                value={state.isNewsletterEnabled}
                                onChange={(newVal: boolean) => {
                                    state.isNewsletterEnabled = newVal;
                                    setState({ ...state, isNewsletterEnabled: newVal });
                                }}
                            />
                        </div>
                    )}

                    <Button
                        appearance="primary"
                        type="button"
                        disabled={state.disableJoinButton || !state.firstName || !state.lastName}
                        onClick={async () => await handleSubmit(identity)}>
                        {joinButtonText}
                    </Button>
                </div>
                {state.error ? (
                    <FooterError error={state.error === 'object' ? JSON.stringify(state.error) : state.error} />
                ) : null}
            </div>
        </Modal>
    );

    function getValidationMessage(state) {
        if (!isValidEmail(state.email)) {
            return t`CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_EMAIL`;
        }

        if (!isValidName(state.firstName)) {
            return t`CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_FIRST_NAME`;
        }

        if (!isValidName(state.lastName)) {
            return t`CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_LAST_NAME`;
        }

        return null;
    }
    function onEmailChange(email: string) {
        setState({ ...state, email: email, error: getValidationMessage(state) });
    }

    function onFirstNameChange(name: string) {
        setState({ ...state, firstName: name, error: getValidationMessage(state) });
    }

    function onLastNameChange(lastName: string) {
        setState({ ...state, lastName: lastName, error: getValidationMessage(state) });
    }

    async function handleSubmit(identity) {
        if (!isValidName(state.firstName) || !isValidName(state.lastName) || !isValidEmail(state.email)) {
            setState({ ...state, error: getValidationMessage(state) });
        }

        var requestModel = {
            id: identity.user.guid,
            agreementId: identity.currentAgreement.number,
            userId: identity.user.id,
            language: identity.locale.language.id,
            countryCode: identity.locale.country.code,
            email: state.email,
            firstName: state.firstName,
            lastName: state.lastName,
        };

        setState({ ...state, disableJoinButton: true });

        await linkCurrentUser(requestModel);
    }
}

const textIds = [
    'CONNECT_VERIFY_DETAILS_MODAL_TITLE',
    'CONNECT_VERIFY_DETAILS_MODAL_TITLE_INVITE',
    'CONNECT_VERIFY_DETAILS_MODAL_SUBTITLE',
    'CONNECT_VERIFY_DETAILS_MODAL_CONTENT',
    'CONNECT_VERIFY_DETAILS_MODAL_CONTENT_INVITE',
    'CONNECT_VERIFY_DETAILS_MODAL_JOIN_CONNECT',
    'CONNECT_VERIFY_DETAILS_MODAL_JOIN_CONNECT_INVITE',
    'CONNECT_VERIFY_DETAILS_MODAL_LABEL_EMAIL',
    'CONNECT_VERIFY_DETAILS_MODAL_LABEL_FIRST_NAME',
    'CONNECT_VERIFY_DETAILS_MODAL_LABEL_LAST_NAME',
    'CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_EMAIL',
    'CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_FIRST_NAME',
    'CONNECT_VERIFY_DETAILS_MODAL_NOT_VALID_LAST_NAME',
    'CONNECT_VERIFY_CHECKBOX1',
    'CONNECT_VERIFY_CHECKBOX2',
    'SHARED_USER_LINKED_TO_VISMA_CONNECT_HEADLINE',
    'SHARED_USER_LINKED_TO_VISMA_CONNECT_DESCRIPTION',
    'SHARED_CONFIRM_YES',
    'SHARED_USER_INVITED_TO_VISMA_CONNECT_HEADLINE',
    'SHARED_USER_INVITED_TO_VISMA_CONNECT_DESCRIPTION',
];
